<script lang="ts" setup>
import {onMounted} from "vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import CustomerSearch from "@/customer/components/customer-search/CustomerSearch.vue";
import HomeLiveActions from "@/home/components/HomeLiveActions.vue";
import {useExperienceStore} from "@/stores/experience.store";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {storeToRefs} from "pinia";
import {useExtensionStore} from "@/stores/extension.store";
import {useCreditStore} from "@/stores/credit.store";
import TheHubspotChat from '@/onboarding/components/hubspot/TheHubspotChat.vue';
import TrialStatus from "@/trial/components/trial-status/TrialStatus.vue";
import HomeSponsorship from "@/home/components/HomeSponsorship.vue";
import AlertBanner from "@/alert/components/AlertBanner.vue";
import HomeWelcome from "@/home/components/HomeWelcome.vue";
import HomeExtensions from "@/home/components/HomeExtensions.vue";
import HomeMenuCard from "@/home/components/HomeMenuCard.vue";
import Icon from "@/core/components/icon/Icon.vue";
import useCurrentFeatureHighlight from "@/home/composables/useCurrentFeatureHighlight";
import HomeFeatureHighlight from "@/home/components/HomeFeatureHighlight.vue";
import HomeFeatureHighlightReplay from "@/home/components/HomeFeatureHighlightReplay.vue";
import HomeNoRecentSync from "@/home/components/HomeNoRecentSync.vue";
import LiveActivation from "@/live/components/LiveActivation.vue";

const experienceStore = useExperienceStore();
const subscriptionPlanStore = useSubscriptionPlanStore();
const extensionStore = useExtensionStore();
const creditStore = useCreditStore();
const {loading: extensionLoading, refreshing} = storeToRefs(extensionStore);

const {data: featureHighlightResponse, isValidating: featureHighlightValidating} = useCurrentFeatureHighlight();

/**
 * How long data asked for by another screen is not asked again.
 *
 * The loading screen reads the credit, the plans and the extensions just
 * before opening this one: without it, every Connect start asked for them
 * twice, in the morning wave when the pharmacies open.
 */
const RECENTLY_LOADED = 30_000;

/**
 * Whether the data was asked for or received a moment ago.
 *
 * @param at When it was, null if never.
 */
const isRecent = (at: number | null): boolean => at !== null && Date.now() - at < RECENTLY_LOADED;

onMounted(() => {
    experienceStore.fetchEvents();
    experienceStore.read();

    if (!isRecent(creditStore.requestedAt)) {
        creditStore.refresh();
    }

    if (!subscriptionPlanStore.loading && !isRecent(subscriptionPlanStore.requestedAt)) {
        subscriptionPlanStore.refreshSubscriptionPlans();
    }

    if (!extensionLoading.value && !refreshing.value && !isRecent(extensionStore.receivedAt)) {
        extensionStore.refreshExtensions();
    }
});
</script>

<template>
  <TheHubspotChat />
  <div>
    <AlertBanner class="mb-4" />
    <BaseContainer class="flex flex-col pb-6 pt-3 gap-6">
      <div class="px-6 space-y-8">
        <HomeNoRecentSync/>
        <LiveActivation />
        <TrialStatus />
        <HomeFeatureHighlight v-if="featureHighlightResponse?.data?.length" :feature-highlight="featureHighlightResponse.data[0]" :validating="featureHighlightValidating" />
        <HomeFeatureHighlightReplay v-else />
        <div class="flex gap-4">
          <HomeWelcome class="flex-1" />
          <CustomerSearch class="bg-white py-5 rounded-lg" placeholder="Rechercher un patient…" />
        </div>
      </div>
      <div class="relative">
        <HomeLiveActions />
      </div>
      <div class="px-6 space-y-6">
        <HomeExtensions />
        <div class="flex flex-col gap-6">
          <router-link :to="{name: 'patient-administration.home'}">
            <HomeMenuCard title="Relation patients" description="Animez et fidélisez vos patients">
              <template #icon>
                <Icon name="mdi-account-multiple" />
              </template>
            </HomeMenuCard>
          </router-link>
          <router-link :to="{name: 'disruption-logistics'}">
            <HomeMenuCard title="Rupture" description="Facilitez la gestion de vos ruptures">
              <template #icon>
                <Icon name="mdi-truck" />
              </template>
            </HomeMenuCard>
          </router-link>
        </div>
        <div class="flex flex-1 items-end">
          <HomeSponsorship class="w-full" />
        </div>
      </div>
    </BaseContainer>
  </div>
</template>
